import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { type Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CreateOptionChip } from '~components/FormFields/CreateOptionChip';
import MaterialOptionSecondary from '~components/FormFields/Parts/MaterialOptionSecondary';
import { SelectOption } from '~components/FormFields/Parts/SelectOption';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { NameIdSchemaRequired } from '~constants/regexConst';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import { BasicMaterial, Material, MaterialTypeahead } from '~hooks/useMaterials/models';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { Order } from '~hooks/useOrders/models';
import { Project, ProjectMaterialTotal } from '~hooks/useProjects/models';
import { AddArrayItemBox } from '~pages/Sales/sharedSales/AddArrayItemBox';
import { FormArrayRowCard } from '~pages/Sales/sharedSales/FormArrayRowCard';
import { makeMaterialRateCardLine } from '~pages/Sales/sharedSales/materialRateLabels';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

// One editable material row on the order (additive, beyond the scalar primary
// `material`). `isDeleted` soft-deletes a persisted row so the deparse can emit
// { id, _destroy }; a physical remove alone can't delete a persisted child.
export type MaterialTotalRowDto = {
  id?: string;
  material: MaterialTypeahead | null;
  unitOfMeasure: NameIdSchemaRequired | null;
  totalQuantity: string | null;
  isDeleted?: boolean;
};

export type MaterialsV2Dto = {
  company: CompanyBasic | null;
  loadsPerTruck: number | null;
  unitsPerHour: number | null;
  unitOfMeasure: NameIdSchemaRequired | null;
  material: MaterialTypeahead | null;
  quantity: number | null;
  materialTotals?: MaterialTotalRowDto[];
};

const EMPTY_PROJECT_MATERIALS: ProjectMaterialTotal[] = [];
const EMPTY_PROJECT_MATERIAL_RATES: MaterialRate[] = [];

// project_material_totals.material is fetched with_discarded on the backend so a
// project's historical line still shows its material after it's discarded. The
// lists this component derives from it feed pickers and seeds, not a read-only
// history view — a discarded material must never be offered or auto-picked.
function isSelectableProjectMaterial(
  material: BasicMaterial | null | undefined,
): material is BasicMaterial {
  return Boolean(material?.id) && !material?.discardedAt;
}

// Same inline cap the dispatch order card applies to its own per-material list,
// so one visual budget covers both surfaces.
const PROJECT_RATE_LINES_INLINE_CAP = 3;

// Its own component so each row keeps its own expanded/collapsed state.
function ProjectMaterialRateLines({ rates }: { rates: MaterialRate[] }) {
  const { currencyFormatter } = useCompanyCurrency();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((expanded) => !expanded);
  }, []);

  const lines = rates
    .map((rate) => ({
      id: rate.id,
      text: makeMaterialRateCardLine(
        {
          buyPrice: rate.buyPrice,
          sellPrice: rate.sellPrice,
          siteName: rate.site?.name ?? null,
          unitOfMeasure: rate.unitOfMeasure,
        },
        currencyFormatter,
      ),
    }))
    .filter((line): line is { id: string; text: string } => line.text !== null);

  if (lines.length === 0) {
    return null;
  }

  const visibleLines = isExpanded ? lines : lines.slice(0, PROJECT_RATE_LINES_INLINE_CAP);
  const hiddenLineCount = lines.length - visibleLines.length;

  return (
    <Grid size={{ xs: 12 }}>
      {visibleLines.map((line) => (
        <Typography
          key={line.id}
          data-test-id="order-material-total-project-rate"
          variant="body2"
          sx={{ color: 'text.secondary', fontStyle: 'italic' }}
        >
          {/* The lead-in and the rate detail stay separate text nodes: i18next
              is configured to HTML-escape interpolated values, so passing the
              composed detail through t() would render its "/" as "&#x2F;". */}
          {t('order.form.project_material_rate')} {line.text}
        </Typography>
      ))}

      {hiddenLineCount > 0 || isExpanded ? (
        <Typography
          data-test-id="order-material-total-project-rate-toggle"
          onClick={toggleExpanded}
          variant="body2"
          sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }}
        >
          {isExpanded
            ? t('actions.show_less')
            : t('order.form.project_material_rates_show_more', {
                count: hiddenLineCount,
              })}
        </Typography>
      ) : null}
    </Grid>
  );
}

interface MaterialsProps {
  /**
   * Material ids allowed under the selected pickup site, or null/undefined when
   * no site filter applies. Owned by OrderFormV3Context so the options filter,
   * the default material pick and the phase cascade all read the same set.
   */
  allowedMaterialIds?: Set<string> | null;
  order?: Order;
  parent?: Project;
  // Rates attach to the order's single scalar `material_id`, never to the additive
  // material_totals rows, so the rates block belongs inside the primary material's card.
  primaryMaterialRatesSlot?: React.ReactNode;
  // Read-only: an order stores a rate for its scalar material only, so these can
  // never become editable fields. The caller narrows them to its customer account.
  projectMaterialRates?: MaterialRate[];
}

export default function Materials({
  allowedMaterialIds,
  order,
  parent,
  primaryMaterialRatesSlot,
  projectMaterialRates = EMPTY_PROJECT_MATERIAL_RATES,
}: MaterialsProps) {
  const form = useFormContext<MaterialsV2Dto>();

  const { userStore, toasterStore } = useStores();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();

  // A company can restrict which units its orders may use, and the
  // autocomplete matches a selection by id against the options it was given —
  // so a seeded unit has to BE one of those options or the field renders blank.
  //
  // Keyed on the ids rather than the array identity: this Map is a dependency of
  // the seeding effect below, which writes form values, so an identity that
  // changes per render would re-run that effect forever.
  const allowedUnitOfMeasureIds = unitOfMeasureOptions.map((option) => option.id).join();
  const allowedUnitOfMeasureOptionsById = useMemo(
    () =>
      new Map<string, NameIdSchemaRequired>(
        unitOfMeasureOptions.map((option) => [option.id, option]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedUnitOfMeasureIds],
  );

  const { createMaterialByCompanyId, getMaterialsByCompanyIdTypeahead } = useMaterials();

  const [notFoundMaterialName, setNotFoundMaterialName] = useState('');
  const [isMaterialLoading, setIsMaterialLoading] = useState(false);

  // A material already in the slot on first render belongs to the project the
  // form opened with, so treat that project as already seeded — otherwise the
  // very first run would overwrite a pre-filled pick.
  const lastSeededProjectIdRef = useRef<string | undefined>(
    form.getValues('material') ? parent?.id : undefined,
  );

  const watchedCompany = form.watch('company');
  const companyId = watchedCompany?.id || userStore.userCompany?.id || '';

  const createMaterial = useCallback(async () => {
    if (!companyId) {
      toasterStore.push(
        alert(t('form_validation_errors.no_user_company'), AlertTypes.error),
      );

      return;
    }

    try {
      setIsMaterialLoading(true);
      const createdMaterial = await createMaterialByCompanyId({
        companyId,
        material: { name: notFoundMaterialName } as unknown as Material,
      });

      form.setValue('material', createdMaterial as unknown as MaterialTypeahead, {
        shouldValidate: true,
        shouldDirty: true,
      });

      toasterStore.push(
        alert(
          t('administration.material.material_created', {
            name: createdMaterial.name,
          }),
          AlertTypes.success,
        ),
      );
    } finally {
      setIsMaterialLoading(false);
    }
  }, [companyId, notFoundMaterialName]);

  const handleMaterialInput = useCallback((_: React.SyntheticEvent, value: string) => {
    setNotFoundMaterialName(value);
  }, []);

  const projectMaterials = parent?.projectMaterialTotals ?? EMPTY_PROJECT_MATERIALS;
  // Judged on selectable (non-discarded) materials, not mere presence: a project
  // whose every material is discarded must fall through to the company-wide
  // typeahead below instead of serving an empty, non-paginated cached list.
  const hasSelectableProjectMaterials = projectMaterials.some((pmt) =>
    isSelectableProjectMaterial(pmt.material),
  );

  // Constrain an options page to the site-scoped allowed set,
  // merging the already-selected material back in so it is never dropped from
  // the open menu — a dropped selected value is silent data-loss.
  const constrainToAllowedMaterials = useCallback(
    (materials: Array<MaterialTypeahead | BasicMaterial>) => {
      if (!allowedMaterialIds) return materials;

      const allowed = materials.filter((material) => allowedMaterialIds.has(material.id));
      const selectedMaterial = form.getValues('material');
      const selectedIsDropped =
        Boolean(selectedMaterial?.id) &&
        !allowed.some((material) => material.id === selectedMaterial?.id);

      return selectedIsDropped && selectedMaterial
        ? [selectedMaterial, ...allowed]
        : allowed;
    },
    [allowedMaterialIds, form],
  );

  const fetchMaterialsCallback = useCallback(
    async (options: { companyId: string; query?: string; cursor?: string }) => {
      const isSearching = !!options.query || !!options.cursor;

      if (!isSearching && hasSelectableProjectMaterials) {
        // Deduplicate by material id: a project carries one material total per
        // (material, PO) line, so a material sold under several POs — the norm
        // once those lines carry pickup sites — otherwise shows up once per line.
        const materialsById = new Map<string, BasicMaterial>();
        for (const projectMaterialTotal of projectMaterials) {
          const material = projectMaterialTotal.material;
          if (isSelectableProjectMaterial(material) && !materialsById.has(material.id)) {
            materialsById.set(material.id, material);
          }
        }
        const materials = Array.from(materialsById.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        return { data: constrainToAllowedMaterials(materials), pagination: null };
      }

      // A vendor-side user managing a customer's order needs to see that
      // order's own materials, not just their own company's — the order-scoped
      // endpoint covers that, falling back to the company-scoped one while the
      // order doesn't exist yet (e.g. a brand-new order being created).
      const response = await getMaterialsByCompanyIdTypeahead({
        ...options,
        orderId: order?.id,
      });
      if (!response || !allowedMaterialIds) return response;

      // Search results and pagination pages are constrained client-side to
      // the same set. Only non-cursor pages merge the selected material back
      // in — cursor pages append to options the field already holds.
      const constrained = options.cursor
        ? response.data.filter((material) => allowedMaterialIds.has(material.id))
        : constrainToAllowedMaterials(response.data);

      return { ...response, data: constrained };
    },
    [
      allowedMaterialIds,
      constrainToAllowedMaterials,
      getMaterialsByCompanyIdTypeahead,
      hasSelectableProjectMaterials,
      order?.id,
      projectMaterials,
    ],
  );

  useEffect(() => {
    if (
      !order?.id &&
      parent?.projectMaterialTotals &&
      parent.projectMaterialTotals.length > 0
    ) {
      // The default pick respects the site-scoped allowed set.
      const defaultCandidates = allowedMaterialIds
        ? parent.projectMaterialTotals.filter((pmt) =>
            pmt.material ? allowedMaterialIds.has(pmt.material.id) : false,
          )
        : parent.projectMaterialTotals;
      if (defaultCandidates.length === 0) return;

      // Seed an empty slot, and reseed when the project itself changes; never
      // overwrite the dispatcher's pick just because the pickup site (and so the
      // allowed set) moved.
      const projectChanged = parent?.id !== lastSeededProjectIdRef.current;
      if (form.getValues('material') && !projectChanged) return;

      const firstMaterial = defaultCandidates.find((projectMaterialTotal) =>
        isSelectableProjectMaterial(projectMaterialTotal.material),
      );
      if (!firstMaterial) {
        // A project switch onto a project with no eligible material (e.g. every
        // material total is discarded) must not leave the previous project's
        // material selected — it would read as this project's own pick. The unit
        // of measure travels with the material, so a lingering one would belong
        // to no material and to a different project.
        if (projectChanged) {
          form.setValue('material', null);
          form.setValue('unitOfMeasure', null);
          lastSeededProjectIdRef.current = parent?.id;
        }

        return;
      }

      form.setValue('material', firstMaterial.material as unknown as MaterialTypeahead);
      lastSeededProjectIdRef.current = parent?.id;

      // A site change can seed the primary after the rows exist, so drop its blank row.
      const primaryMaterialId = firstMaterial.material?.id;
      form.setValue(
        'materialTotals',
        (form.getValues('materialTotals') ?? []).filter(
          (row) => row.material?.id !== primaryMaterialId || Boolean(row.totalQuantity),
        ),
      );

      // The project cascade also writes this field, so only fill a blank one. The
      // empty slot holds an object rather than null, so blankness is judged on the id.
      const projectUnitOfMeasureId = firstMaterial.unitOfMeasure?.id;
      const allowedProjectUnitOfMeasure = projectUnitOfMeasureId
        ? allowedUnitOfMeasureOptionsById.get(projectUnitOfMeasureId)
        : undefined;

      if (allowedProjectUnitOfMeasure && !form.getValues('unitOfMeasure')?.id) {
        form.setValue('unitOfMeasure', allowedProjectUnitOfMeasure);
      }
    }
    // Depends on the setValue/getValues handles rather than the whole form object:
    // the form identity changes between renders, and re-running this would overwrite
    // a pick the dispatcher already made.
  }, [
    allowedMaterialIds,
    allowedUnitOfMeasureOptionsById,
    form.getValues,
    form.setValue,
    order?.id,
    parent,
  ]);

  const {
    fields: materialTotalFields,
    append: appendMaterialTotal,
    remove: removeMaterialTotal,
    update: updateMaterialTotal,
  } = useFieldArray({ control: form.control, name: 'materialTotals' });

  // A project total is the whole-job plan while an order is one day of it, so the
  // quantity is entered per order and only the unit carries over. The deparse drops
  // any row left at blank quantity, so an untouched seeded material never persists.
  useEffect(() => {
    // With no project selected there is nothing to seed FROM, and the list is
    // whatever the dispatcher typed by hand — so leave it alone entirely. Only a
    // selected project owns this array.
    if (order?.id || !parent) {
      return;
    }
    // A selected project with no materials of its own still has to clear whatever
    // the previous project seeded. Returning early on an empty list left the old
    // project's materials in the form, and they would submit with the order — so
    // this writes the empty list rather than bailing out.
    const projectTotals = parent.projectMaterialTotals ?? EMPTY_PROJECT_MATERIALS;
    // Must run after the primary seeding effect above to see the primary it seeds.
    const primaryMaterialId = form.getValues('material')?.id;
    const seenMaterialIds = new Set<string>();
    const seededRows: MaterialTotalRowDto[] = [];
    for (const projectTotal of projectTotals) {
      const material = projectTotal.material;
      // First occurrence wins for the whole row, unit included: two phases may
      // plan the same material in different units, and there is no basis for
      // preferring the later one — so a differing unit further down the list is
      // skipped along with the rest of its duplicate row.
      if (
        !isSelectableProjectMaterial(material) ||
        material.id === primaryMaterialId ||
        seenMaterialIds.has(material.id)
      ) {
        continue;
      }
      seenMaterialIds.add(material.id);
      const projectUnitOfMeasureId = projectTotal.unitOfMeasure?.id;
      const seededUnitOfMeasure = projectUnitOfMeasureId
        ? // A unit the company no longer allows is unselectable, so seeding it
          // would show a value the user cannot re-pick after clearing it. Leave
          // the field empty instead and let them choose an allowed unit.
          (allowedUnitOfMeasureOptionsById.get(projectUnitOfMeasureId) ?? null)
        : null;
      seededRows.push({
        material: material as unknown as MaterialTypeahead,
        totalQuantity: '',
        unitOfMeasure: seededUnitOfMeasure,
      });
    }
    form.setValue('materialTotals', seededRows);
    // Depends on the setValue/getValues handles rather than the whole form object: the
    // form identity can change between renders, and re-running this would overwrite
    // rows the dispatcher had already typed.
  }, [allowedUnitOfMeasureOptionsById, form.getValues, form.setValue, order?.id, parent]);

  // The rate line follows the row's LIVE material selection, not the value the
  // row was appended with.
  const watchedMaterialTotals = useWatch({
    control: form.control,
    name: 'materialTotals',
  });

  // The rate catalog keys a rate by material AND site, so one material can carry
  // several project rates. The backend picks the winner by specificity when it
  // prices a load; naming a winner here would mean re-implementing that ranking,
  // so every candidate is listed and the site labels do the disambiguating.
  const projectRatesByMaterialId = useMemo(() => {
    const ratesByMaterialId = new Map<string, MaterialRate[]>();
    for (const projectMaterialRate of projectMaterialRates) {
      const materialId = projectMaterialRate.material?.id;
      if (!materialId) {
        continue;
      }
      const alreadyCollected = ratesByMaterialId.get(materialId);
      if (alreadyCollected) {
        alreadyCollected.push(projectMaterialRate);
      } else {
        ratesByMaterialId.set(materialId, [projectMaterialRate]);
      }
    }

    // Sorted so the lines never reshuffle between renders: the project-wide rate
    // (no site, hence the empty sort key) first, then by site name, then by id so
    // two rates on the same site still hold a fixed order.
    for (const rates of ratesByMaterialId.values()) {
      rates.sort((left, right) => {
        const leftSiteName = left.site?.name ?? '';
        const rightSiteName = right.site?.name ?? '';

        return (
          leftSiteName.localeCompare(rightSiteName) || left.id.localeCompare(right.id)
        );
      });
    }

    return ratesByMaterialId;
  }, [projectMaterialRates]);

  const handleAddMaterialTotal = useCallback(() => {
    appendMaterialTotal({ material: null, totalQuantity: '', unitOfMeasure: null });
  }, [appendMaterialTotal]);

  const handleRemoveMaterialTotal = useCallback(
    (index: number) => {
      const row = form.getValues(`materialTotals.${index}`);
      if (row?.id) {
        // Persisted row — keep it and flag it so the deparse emits { id, _destroy }.
        updateMaterialTotal(index, { ...row, isDeleted: true });
      } else {
        removeMaterialTotal(index);
      }
    },
    [form, updateMaterialTotal, removeMaterialTotal],
  );

  // The carded primary row lines its columns up with the additive rows below it.
  const primaryFieldWidths = { material: 6, quantity: 3, unitOfMeasure: 3 };

  // A bare divider under a lone material row would look broken, so the rates
  // section only contributes chrome when its content was actually passed in.
  const hasPrimaryMaterialRates =
    primaryMaterialRatesSlot !== undefined && primaryMaterialRatesSlot !== null;

  const primaryMaterialFields = (
    <>
      <Grid size={{ xs: primaryFieldWidths.material }}>
        <AutocompleteAsyncFormField
          asyncCallback={fetchMaterialsCallback}
          control={form.control}
          errors={form.formState.errors}
          extraRequestOptions={{ companyId }}
          getLabel={(material?: MaterialTypeahead) => material?.name ?? ''}
          getValue={(material?: MaterialTypeahead) => material?.id ?? ''}
          label={`${t('form_fields.material')}`}
          name="material"
          listboxWidth="500px"
          clearable
          placeholder={t('form_fields.search_material_or_ext_id')}
          renderOption={(props, option, state) => {
            // MUI v6 folds `key` into the option props; lift it onto the
            // outermost element so React's unique-key warning doesn't fire.
            const { key, ...optionProps } = props;

            return (
              <SelectOption
                key={key as Key}
                isCheckEnabled={false}
                getLabel={(item) => item?.name ?? ''}
                inputValue={state?.inputValue ?? ''}
                option={option}
                props={optionProps}
                secondary={
                  <MaterialOptionSecondary
                    material={option as MaterialTypeahead | BasicMaterial}
                    query={state?.inputValue ?? ''}
                  />
                }
              />
            );
          }}
          noOptionsText={
            // Create-new is disabled under an active site filter — a just-created
            // material cannot belong to the project's site-scoped material totals.
            notFoundMaterialName && !allowedMaterialIds ? (
              <CreateOptionChip
                disabled={isMaterialLoading}
                name={notFoundMaterialName || ''}
                onClick={createMaterial}
              />
            ) : (
              t('form_fields.no_options')
            )
          }
          onInput={handleMaterialInput}
        />
      </Grid>

      <Grid size={{ xs: primaryFieldWidths.quantity }}>
        <NumberInput
          control={form.control}
          label={`${t('form_fields.ordered_quantity')}`}
          name="quantity"
        />
      </Grid>

      <Grid size={{ xs: primaryFieldWidths.unitOfMeasure }}>
        <AutocompleteFormField
          control={form.control}
          errors={form.formState.errors}
          getLabel={(item) => item?.name ?? ''}
          getValue={(item) => item?.id ?? ''}
          label={`${t('form_fields.units')}`}
          list={unitOfMeasureOptions}
          name="unitOfMeasure"
          clearable
        />
      </Grid>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* The order's scalar material and its rates are one linked unit, so they
          share a card; the additive rows below are rate-less siblings. */}
      <FormArrayRowCard dataTestId="order-primary-material-row">
        {primaryMaterialFields}

        {hasPrimaryMaterialRates ? (
          <>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1.5 }} />
            </Grid>

            <Grid size={{ xs: 12 }}>{primaryMaterialRatesSlot}</Grid>
          </>
        ) : null}
      </FormArrayRowCard>

      <Box>
        {materialTotalFields.map((field, index) => {
          if (form.getValues(`materialTotals.${index}.isDeleted`)) {
            return null;
          }

          const rowMaterialId = watchedMaterialTotals?.[index]?.material?.id;
          const rowProjectRates = rowMaterialId
            ? projectRatesByMaterialId.get(rowMaterialId)
            : undefined;

          return (
            <FormArrayRowCard
              key={field.id}
              dataTestId="order-material-total-row"
              onRemove={() => handleRemoveMaterialTotal(index)}
              removeIconDataTestId="order-material-total-remove"
            >
              <Grid size={{ xs: 6 }}>
                <AutocompleteAsyncFormField
                  asyncCallback={fetchMaterialsCallback}
                  control={form.control}
                  errors={form.formState.errors}
                  extraRequestOptions={{ companyId }}
                  getLabel={(material?: MaterialTypeahead) => material?.name ?? ''}
                  getValue={(material?: MaterialTypeahead) => material?.id ?? ''}
                  label={`${t('form_fields.material')}`}
                  name={`materialTotals.${index}.material`}
                  listboxWidth="500px"
                  clearable
                  placeholder={t('form_fields.search_material_or_ext_id')}
                />
              </Grid>

              <Grid size={{ xs: 3 }}>
                <NumberInput
                  control={form.control}
                  label={`${t('form_fields.ordered_quantity')}`}
                  name={`materialTotals.${index}.totalQuantity`}
                />
              </Grid>

              <Grid size={{ xs: 3 }}>
                <AutocompleteFormField
                  control={form.control}
                  errors={form.formState.errors}
                  getLabel={(item) => item?.name ?? ''}
                  getValue={(item) => item?.id ?? ''}
                  label={`${t('form_fields.units')}`}
                  list={unitOfMeasureOptions}
                  name={`materialTotals.${index}.unitOfMeasure`}
                  clearable
                />
              </Grid>

              {rowProjectRates ? (
                <ProjectMaterialRateLines rates={rowProjectRates} />
              ) : null}
            </FormArrayRowCard>
          );
        })}

        <AddArrayItemBox
          title={t('form_fields.add_material')}
          onAdd={handleAddMaterialTotal}
          dataTestId="order-material-total-add"
        />
      </Box>
    </Box>
  );
}
